import React, { Component } from 'react';
import moment from 'moment';
import './account.scss';
import './accountContainer.scss';

import FlipCard, {FlipCardFront} from '../../../components/FlipCard/FlipCard';
import infoIcon from '../../../assets/images/info-icon.svg';
import chevDown from '../../../assets/images/chev-down-royal.svg';
import chevUp from '../../../assets/images/chev-up.svg';
import {formatCurrency} from '../../../utils/helpers';
import withFeatureFlags from '../../FeatureFlags/withFeatureFlags';
import Button from '../../../components/Form/Button/Button';
import AccountActivity from '../AccountActivity/AccountActivity';
import '../../SharedComponents/button-styles.scss';
import AccountDetails from "./AccountDetails";
import { useUnifiedAnalytics } from '../../../contexts/AnalyticsWrapper';

/** @import { WithFeatureFlagsProps } from '../../FeatureFlags/FeatureFlagsTypes' */
/** @import { AccountFRSProps,AccountFRSState } from './AccountFRSTypes' */

/**
 * @typedef {Object} WithAnalyticsProps
 * @property {import('../../../contexts/AnalyticsWrapperTypes').UnifiedAnalytics} analytics
 */

/** @extends {Component<AccountFRSProps & WithFeatureFlagsProps & WithAnalyticsProps, AccountFRSState>} */
class AccountFRS extends Component {
    /** @type {AccountFRSState} */
    state = {
        flipped: false,
        action: null,
        accountActivitiesEnabled: false,
        showPopupInline: null
    };

    componentDidMount() {
        //document.body.addEventListener('click', this.onClickOutside, false);
    };

    componentWillUnmount() {
        //document.body.removeEventListener('click', this.onClickOutside);
    };

    toggleFrsFundingWindowInfo = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) => {
        event.preventDefault();

        if (!this.props.showPopup(event, () => this.renderFrsFundingWindowInfo())) {
            this.setState(({showPopupInline}) => ({
                showPopupInline: showPopupInline ? null : 'FUNDING_WINDOW'
            }))
        }
    };

    toggleFrsFundedInfo = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) => {
        event.preventDefault();

        if (!this.props.showPopup(event, () => this.renderFrsFundedInfo())) {
            this.setState(({showPopupInline}) => ({
                showPopupInline: showPopupInline ? null : 'FUNDED'
            }))
        }
    };

    onSelectActivity = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) => {
        event.preventDefault();

        const {account, onSelectActivity} = this.props;

        this.setState(({accountActivitiesEnabled}) => ({
            accountActivitiesEnabled: !accountActivitiesEnabled
        }), () => {
            if (typeof onSelectActivity === 'function') onSelectActivity(account);
        });
    };

    startAction(/** @type {string} */ action) {
        this.setState({action}, () => {
            setTimeout(() => {
                this.setState({flipped: true}, () => {
                    this.onActionStart();
                });
            }, 1);
        });
    };

    startWithdrawal = () => {
        this.startAction('withdraw');
    };

    startTransfer = () => {
        this.startAction('transfer');
    };

    startDeposit = () => {
        this.startAction('deposit');
    };

    unflipCard = () => {
        const {account, onActionComplete} = this.props;
        this.setState({flipped: false}, () => {
            if (typeof onActionComplete === 'function') onActionComplete(account);
        });
    };

    onActionStart = () => {
        const {account, onActionStart,analytics} = this.props;
        
        if(analytics){
            analytics.trackAnalyticsClickTransactionEvent(
                'start',
                account.product.code,
                this.state.action || '',
                'fixed rate saver',
                `dashboard|click|${account.product.code}-move-money`
            );
        }
        if (typeof onActionStart === 'function') onActionStart(account);
    };

    onAfterFlip = (/** @type {boolean} */ flipped) => {
        if (!flipped) {
            this.setState({action: null});
        }
    };

    accountIsClosed = (/** @type {string} */ status) => {
        return status === "ClosureRequested" || status === "Closed";
    };

    renderClosureMessage = (/** @type {string} */ status) => {
        if (status === "ClosureRequested")
            return 'CLOSURE IN PROGRESS';

        if (status === "Closed")
            return "CLOSED";

        return '';
    };

    renderAccountLink(/** @type {string} */ className) {
        const {activityIsShowing} = this.props;
        let activeClass = activityIsShowing ? 'account__link--active' : '';
        return (
            <a href="#" className={["account__link", activeClass, className].join(' ')} onClick={this.onSelectActivity}>
                <span>Account activity</span>
                {this.state.accountActivitiesEnabled && <img className="account__link-icon" src={chevUp}/>}
                {!this.state.accountActivitiesEnabled && <img className="account__link-icon" src={chevDown}/>}
            </a>
        );
    };

    renderBlendedRate() {
        const {account} = this.props;
        return (
            <>
                <h3 className="account__blended-rate-title">What's the interest rate?</h3>
                <ul className="account__blended-rate-items">
                    {
                        account.product.interestRateTiers && account.product.interestRateTiers
                            .sort((a, b) => a.minBalance > b.minBalance ? -1 : 1)
                            .map((tier, index) => {
                                return tier.rate === 0.00
                                    ? (<li className="account__blended-rate-item" key={index}>However, if it's equal to
                                        or under {formatCurrency(tier.maxBalance)}, you won't earn any interest.</li>)
                                    : (<li className="account__blended-rate-item" key={index}>You'll
                                        earn {tier.rate.toFixed(2)}%^ on your <b>entire</b> balance when it's
                                        between {formatCurrency(tier.minBalance)} - {formatCurrency(tier.maxBalance)}.
                                    </li>)
                            })
                    }
                    <li className="account__blended-rate-item" key={account.product.interestRateTiers && account.product.interestRateTiers.length}>^This is
                        gross p.a., which is the rate you earn per year before tax.
                    </li>
                </ul>
            </>
        );
    };

    renderFrsFundingWindowInfo() {
        return (
            <>
                <h3 className="account__blended-tooltip-title">Why do I have limited time?</h3>
                <ul className="account__blended-tooltip-items">
                    <li className="account__blended-tooltip-item">Your funding window is the seven-day period during
                        which you can make deposits into this account. You won't be able to make any withdrawals during
                        this time, and when it ends you won't be able to deposit any more money.
                    </li>
                    <li className="account__blended-tooltip-item">If you don’t deposit the minimum £5,000 during this
                        time, we’ll close the account and return any money to your linked account for you.
                    </li>
                </ul>
            </>
        );
    };

    renderFrsFundedInfo() {
        return (
            <>
                <h3 className="account__blended-tooltip-title">What does this mean?</h3>
                <ul className="account__blended-tooltip-items">
                    <li className="account__blended-tooltip-item">We'll automatically transfer your total balance,
                        including your interest, back to your linked account on this date.
                    </li>
                </ul>
            </>
        );
    };

    renderInfoSection() {
        const {account, featureEnabled} = this.props;
        if (account.accountStatus === "FundingOpen") {
            return this.renderFrsFundingWindowOpenedInfoSection();
        } else if (account.accountStatus === "Open") {
            return this.renderFrsFundedInfoSection();
        } else if (account.accountStatus === "ClosureRequested") {
            return this.renderClosureMessageSection();
        } else if (account.accountStatus === "Closed") {
            return this.renderClosureMessageSection();
        }
    };

    calculateFundingTimeLeft() {
        const {account} = this.props;

        if (!account.fundingWindowMinutesLeft)
            return "";

        var delta = account.fundingWindowMinutesLeft * 60;

        // calculate (and subtract) whole days
        var days = Math.floor(delta / 86400);
        delta -= days * 86400;

        // calculate (and subtract) whole hours
        var hours = Math.floor(delta / 3600) % 24;
        delta -= hours * 3600;

        // calculate (and subtract) whole minutes
        var minutes = Math.floor(delta / 60) % 60;
        delta -= minutes * 60;

        // build up the FW string
        let dayString = "0 days, ";
        let hourString = "0 hours";
        let minutesString = "0 minutes";

        if (days > 0) {
            dayString = days === 1 ? '1 day ' : `${days} days, `;
        }

        if (hours > 0) {
            hourString = hours === 1 ? '1 hour' : `${hours} hours`;
        }

        if (minutes > 0) {
            minutesString = minutes === 1 ? '1 minute' : `${minutes} minutes`;
        }

        return `${dayString}${hourString} ${minutesString}`;
    }

    renderFrsFundingWindowOpenedInfoSection() {
        const {account} = this.props;

        const fundingWindow = account.fundingEndDate ? this.calculateFundingTimeLeft() : '-';
        return (
            <div className="account__actions">
                <div>
                    <span className="account__info-key account__info-key-blue">Time left to add funds</span>
                    <span className="account__info-value">
                        <a href="#" onClick={this.toggleFrsFundingWindowInfo}><img className="account__interest-account-info" src={infoIcon}/></a>
                    </span>
                    {this.state.showPopupInline === 'FUNDING_WINDOW' && (
                        <div className="account__inline-popup is-open">
                            {this.renderFrsFundingWindowInfo()}
                        </div>
                    )}
                </div>
                <div>
                    <span className="account__info-value">{fundingWindow}</span>
                </div>
            </div>
        );
    };

    formatMaturityDate() {
        const {account} = this.props;
        return account.maturity ? moment(new Date(account.maturity.maturityDate)).format("DD MMMM YYYY") : '';
    };

    goToGiveInstruction(/** @type {string} */ id, /** @type {string} */ product) {
        const {account, onActionStart,analytics} = this.props;
        
        if(analytics) {
            analytics.trackAnalyticsClickEvent(
                "view your options",
                `/maturity-instruction/${id}`,
                "dashboard account card",
                product,
                "maturity",
                {},
                "link click",
                "navigation|click|view your options",
                "Maturity Instruction"
            );
        }
        
        window.location.href = `/maturity-instruction/${id}`;
    }

    renderFrsFundedInfoSection() {
        const {account} = this.props;
        const maturityDate = account.maturity && account.maturity.maturityDate ? this.formatMaturityDate() : '-';
        const isMaturing = account.maturity ? account.maturity.maturityInstructionWindowStatus === "InstructionOpen" : false;

        return (
            <div className="account__actions">
                <div className="account__section-right-space">
                    <span className="account__info-key account__info-key-blue">Your term ends on</span>
                    <span className="account__info-value">
                        <a href="#" onClick={this.toggleFrsFundedInfo}><img className="account__interest-account-info" src={infoIcon}/></a>
                    </span>
                    {this.state.showPopupInline === 'FUNDED' && (
                        <div className="account__inline-popup is-open">
                            {this.renderFrsFundedInfo()}
                        </div>
                    )}
                </div>
                <div className="account__section-right-space">
                    <span className="account__info-value">{maturityDate}</span>
                </div>
                {(isMaturing && account.maturity && !account.maturity.maturityInstruction) &&
                    <Button size='med' onClick={() => this.goToGiveInstruction(account.id, account.product.code)} label="View your options"/>}
                {account.maturity && account.maturity.maturityInstructionWindowStatus && account.maturity.maturityInstruction &&
                    <Button
                        size='med'
                        onClick={() => {
                            const {analytics} = this.props;
                            const productName = account.product.code;
                            if(analytics) {
                                analytics.trackAnalyticsClickEvent(
                                    'view your instruction',
                                    `account-details/${account.id}`,
                                    'dashboard',
                                    productName,
                                    '',
                                    {},
                                    'link click',
                                    `dashboard|click|${productName}-view-your-instruction`,
                                    'dashboard'
                                );
                            }
                            window.location.href = "account-details/" + account.id;
                        }}
                        label="View your instruction"
                    />}
            </div>
        );
    };

    renderClosureMessageSection() {
        const {account} = this.props;
        return (
            <div className="account__actions">
                <p className="account__closed-message">{this.renderClosureMessage(account.accountStatus)}</p>
            </div>
        );
    };

    renderAccountFront() {
        const {account, customer} = this.props;

        const isMaturing = account.maturity && account.maturity.maturityInstructionWindowStatus === "InstructionOpen" && !this.accountIsClosed(account.accountStatus)

        return (
            <div className="accountcontainer">
                <div className={this.accountIsClosed(account.accountStatus) ? "account__front account__front--closed" : "account__front"}>
                    {isMaturing ?
                        <AccountDetails account={account} customer={customer} overrideAccountName={`${account.product.name} (maturing)`}/> :
                        <AccountDetails account={account} customer={customer}/>
                    }
                    {/*<div className="account__divider"/>*/}
                    <div className="account__section-right">
                        <div className="account__balance">
                            <span className="account__balance-key">Balance</span>
                            <span className="account__balance-value" data-accountbalance-number={account.accountNumber}>{formatCurrency(account.balance)}</span>
                        </div>
                        {this.renderAccountLink('account__link--desktop')}
                    </div>
                    {/*<div className="account__divider"/>*/}
                    {this.renderInfoSection()}
                    {this.renderAccountLink('account__link--mobile')}
                </div>
            </div>
        );
    };

    getClassNames() {
        const {hide, disable} = this.props;
        return ['account', hide ? 'account--hidden' : '', disable ? 'account--disabled' : ''].join(' ');
    };

    render() {
        const {flipped} = this.state;
        const {account, accountWithActionInProgress} = this.props;

        return (
            <>
                <div className={this.getClassNames()}>
                    <FlipCard flipped={flipped} onAfterFlip={this.onAfterFlip}>
                        <FlipCardFront>{this.renderAccountFront()}</FlipCardFront>
                    </FlipCard>
                </div>
                {this.state.accountActivitiesEnabled &&
                    <AccountActivity activityRef={this.props.activityRef} account={account} disable={Boolean(accountWithActionInProgress)}/>}
            </>
        );
    };
}

/**
 * Higher-order component that injects analytics
 * @param {React.ComponentType<any>} Component - Component that needs analytics
 * @returns {React.ComponentType<any>} - Wrapped component with analytics injected
 */
const WithAnalytics = (Component) => {
    /**
     * @param {Object} props - Component props without analytics
     * @returns {JSX.Element}
     */
    return function WrappedComponent(props) {
        const analytics = useUnifiedAnalytics();
        return <Component {...props} analytics={analytics} />;
    }
};

export default WithAnalytics(withFeatureFlags(AccountFRS));
